import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@shared/lib/supabase';
import { useAuth } from '@dashboard/hooks/useAuth';

export default function StripeOnboardingComplete() {
  const { user, loading: authLoading } = useAuth();
  const [stripeOnboarded, setStripeOnboarded] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(false);
  const [hoveredBtn, setHoveredBtn] = useState<string | null>(null);

  const checkStatus = useCallback(async () => {
    if (!user) return;
    setChecking(true);
    const { data } = await supabase
      .from('merchants')
      .select('stripe_onboarded')
      .eq('id', user.id)
      .maybeSingle();
    if (data) setStripeOnboarded(data.stripe_onboarded ?? false);
    setChecking(false);
  }, [user]);

  useEffect(() => {
    if (!authLoading && user) checkStatus();
  }, [authLoading, user, checkStatus]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--dash-bg, #09090b)' }}>
        <p className="font-sans text-sm" style={{ color: 'var(--dash-text-dim)' }}>Loading...</p>
      </div>
    );
  }

  const isActive = stripeOnboarded === true;

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: 'var(--dash-bg, #09090b)' }}>
      <div className="w-full max-w-md text-center">
        <Link
          to="/"
          className="font-mono tracking-widest text-sm font-medium uppercase"
          style={{ color: 'var(--dash-text-primary)' }}
        >
          BONESPAY
        </Link>
        <div
          className="mt-10 rounded p-8 md:p-10"
          style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}
        >
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{ background: 'var(--dash-hover-bg)' }}
          >
            {isActive ? (
              <span className="text-emerald-400 text-2xl">&#10003;</span>
            ) : (
              <span className="text-amber-400/70 text-2xl">&#9655;</span>
            )}
          </div>

          {isActive ? (
            <>
              <h2
                className="font-serif text-2xl mb-3"
                style={{ color: 'var(--dash-text-primary)' }}
              >
                Stripe Connected
              </h2>
              <p
                className="font-sans text-sm leading-relaxed mb-6"
                style={{ color: 'var(--dash-text-muted)' }}
              >
                Your Stripe account is active and ready to accept card payments.
                You can now process transactions through your WooCommerce store.
              </p>
              <Link
                to="/dashboard"
                className="inline-block bg-blue-600 text-white font-mono text-sm font-medium px-8 py-3 rounded hover:bg-blue-500 transition-colors uppercase tracking-wider"
              >
                Go to Dashboard
              </Link>
            </>
          ) : (
            <>
              <h2
                className="font-serif text-2xl mb-3"
                style={{ color: 'var(--dash-text-primary)' }}
              >
                Account Under Review
              </h2>
              <p
                className="font-sans text-sm leading-relaxed mb-6"
                style={{ color: 'var(--dash-text-muted)' }}
              >
                Your Stripe account is being reviewed. This usually takes a few minutes.
                You'll receive an email from Stripe when your account is ready to accept payments.
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={checkStatus}
                  disabled={checking}
                  className="inline-flex items-center justify-center gap-2 font-mono text-sm px-6 py-3 rounded transition-colors disabled:opacity-50 uppercase tracking-wider"
                  style={{
                    border: '1px solid var(--dash-border)',
                    color: hoveredBtn === 'refresh' ? 'var(--dash-text-secondary)' : 'var(--dash-text-tertiary)',
                    background: hoveredBtn === 'refresh' ? 'var(--dash-hover-bg)' : 'transparent',
                  }}
                  onMouseEnter={() => setHoveredBtn('refresh')}
                  onMouseLeave={() => setHoveredBtn(null)}
                >
                  <span className={checking ? 'inline-block animate-spin' : ''}>&#8635;</span>
                  {checking ? 'Checking...' : 'Refresh Status'}
                </button>
                <Link
                  to="/dashboard"
                  className="inline-block bg-blue-600 text-white font-mono text-sm font-medium px-8 py-3 rounded hover:bg-blue-500 transition-colors uppercase tracking-wider"
                >
                  Go to Dashboard
                </Link>
              </div>
            </>
          )}

          <div className="pt-6 mt-6" style={{ borderTop: '1px solid var(--dash-border)' }}>
            <p
              className="font-mono text-[10px] uppercase tracking-widest"
              style={{ color: 'var(--dash-text-faint)' }}
            >
              Stripe onboarding status updates automatically
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
